import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { AuthShell } from "./auth-shell";

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState<"idle" | "busy" | "done" | "error">(
    token.trim() ? "busy" : "idle",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token.trim()) return;
    let cancelled = false;
    setStatus("busy");
    setError(null);
    void api<
      | { user: unknown }
      | { requiresTwoFactor: true; challengeId: string; methods: string[] }
    >("/api/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
    })
      .then((result) => {
        if (cancelled) return null;
        if ("requiresTwoFactor" in result) {
          // Enrolled second factor still applies: continue on the login page.
          navigate(
            `/login?challenge=${encodeURIComponent(result.challengeId)}`,
            { replace: true },
          );
          return null;
        }
        return refresh();
      })
      .then((value) => {
        // A 2FA redirect navigates away; skip the "done" state in that case.
        if (!cancelled && value !== null) setStatus("done");
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setStatus("error");
          setError(
            err instanceof Error
              ? err.message
              : "Verification link is invalid or expired",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, refresh, navigate]);

  useEffect(() => {
    if (status !== "done") return;
    const t = setTimeout(() => navigate("/", { replace: true }), 1500);
    return () => clearTimeout(t);
  }, [status, navigate]);

  return (
    <AuthShell
      title="Verify your email"
      description="Confirming your email address…"
      belowPanel={
        <p className="text-center text-sm text-rs-ink-muted">
          <Link
            to="/login"
            className="font-medium underline-offset-2 hover:underline"
          >
            Back to Sign In
          </Link>
        </p>
      }
    >
      {!token.trim() ? (
        <div className="space-y-4">
          <p className="text-sm text-rs-danger" role="alert">
            This verification link is missing or incomplete. Request a new one
            from the sign-in page.
          </p>
          <Button asChild className="rs-auth-submit rs-auth-submit-block">
            <Link to="/login">Back to Sign In</Link>
          </Button>
        </div>
      ) : status === "done" ? (
        <p className="text-sm text-rs-ink" role="status">
          Email verified. Signing you in…
        </p>
      ) : status === "error" ? (
        <div className="space-y-4">
          <p className="text-sm text-rs-danger" role="alert">
            {error ?? "Verification link is invalid or expired"}
          </p>
          <Button asChild className="rs-auth-submit rs-auth-submit-block">
            <Link to="/login">Back to Sign In</Link>
          </Button>
        </div>
      ) : (
        <p className="text-sm text-rs-ink-muted" role="status">
          Verifying…
        </p>
      )}
    </AuthShell>
  );
}
